import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';

import { Icon, type IconName } from './Icon.js';
import { SEND_MESSAGE } from '../util/device.js';
import { fillFromMarkup, serializeToMarkup } from '../util/htmlMarkup.js';
import styles from './RichTextEditor.module.css';

interface Tool {
  icon: IconName;
  label: string;
  /** The execCommand this button runs, which also identifies it. */
  command: string;
}

const TOOLS: Tool[] = [
  { icon: 'bold', label: 'Bold', command: 'bold' },
  { icon: 'italic', label: 'Italic', command: 'italic' },
  { icon: 'strikethrough', label: 'Strikethrough', command: 'strikeThrough' },
  { icon: 'list', label: 'Bulleted list', command: 'insertUnorderedList' },
  { icon: 'listOrdered', label: 'Numbered list', command: 'insertOrderedList' },
];

interface RichTextEditorProps {
  /** Fires with the message's markup (see `htmlMarkup.ts`), not with HTML. */
  onChange: (markup: string) => void;
  placeholder?: string;
  disabled?: boolean;
  autoFocus?: boolean;
  /**
   * Pinned to the toolbar's right end, past the tools. For a control that acts
   * on the editor as a whole rather than on the words in it.
   */
  toolbarEnd?: ReactNode;
  /**
   * Inside the box, under the toolbar and above the field: whatever is being
   * sent along with the words. A staged quote goes here rather than above the
   * box, because sharing the container is the thing that says the two will be
   * sent together.
   */
  staged?: ReactNode;
  /**
   * Inside the box, below the field: the controls that act on the draft as a
   * whole. Sending belongs in here and not under the box, because the box is
   * the composer — a send button sitting outside it reads as a button that
   * belongs to the page.
   */
  foot?: ReactNode;
  /**
   * ⌘/Ctrl+Enter. Enter on its own stays a new line, always — the reason to be
   * in an editor this size is to write something with lines in it.
   */
  onSubmit?: () => void;
}

export interface RichTextEditorHandle {
  /** Replace the draft with this markup, caret at the end. */
  setMarkup: (markup: string) => void;
}

/**
 * A small WYSIWYG composer: what is bold looks bold while it is typed, and the
 * value that comes out is the plain markup string the message will carry.
 *
 * Uncontrolled on purpose. The caret lives in the DOM the browser is editing,
 * and writing a re-serialised value back into it on every keystroke is what
 * makes home-grown editors jump the cursor to the end. So the DOM is the draft,
 * `onChange` reports what it currently means, and nothing writes back — with one
 * exception, `setMarkup`, which is a deliberate handover from somewhere else and
 * puts the caret at the end itself.
 *
 * `execCommand` is formally deprecated and still the only thing every browser
 * implements for this; the day it goes, the replacement changes this file and
 * nothing downstream, because what leaves here is a string.
 */
export const RichTextEditor = forwardRef<RichTextEditorHandle, RichTextEditorProps>(
  function RichTextEditor(
    { onChange, placeholder, disabled, autoFocus, toolbarEnd, staged, foot, onSubmit },
    ref,
  ) {
    const fieldRef = useRef<HTMLDivElement>(null);
    const [empty, setEmpty] = useState(true);
    const [active, setActive] = useState<string[]>([]);

    useEffect(() => {
      // Tags rather than inline styles, so the serialiser reads <b> and not a
      // span carrying font-weight.
      document.execCommand('styleWithCSS', false, 'false');
      if (autoFocus) fieldRef.current?.focus();
    }, [autoFocus]);

    const emit = () => {
      const node = fieldRef.current;
      if (!node) return;
      setEmpty(isBlank(node));
      onChange(serializeToMarkup(node));
    };

    /**
     * The current `emit`, for the handle below to call without capturing it.
     *
     * `emit` closes over this render's `onChange`, so a handle built once would
     * report drafts to a callback that had since been replaced. Reading it through
     * a ref keeps the handle itself stable: without this it needed no dependency
     * array, which rebuilt the object and its closures on every render — and while
     * the frame is open, every keystroke is a render.
     */
    const emitRef = useRef(emit);
    emitRef.current = emit;

    useImperativeHandle(
      ref,
      () => ({
        setMarkup: (markup: string) => {
          const node = fieldRef.current;
          if (!node) return;
          fillFromMarkup(node, markup);
          emitRef.current();
          caretToEnd(node);
        },
      }),
      [],
    );

    /**
     * Which marks the caret is currently inside, for the pressed look. Runs on
     * every keystroke, so it hands back the previous array when nothing changed:
     * a fresh array each time re-rendered the toolbar and its five icons on every
     * character typed, mid-word, with no mark having moved.
     */
    const readState = () => {
      const next = TOOLS.filter((tool) => document.queryCommandState(tool.command)).map(
        (tool) => tool.command,
      );
      setActive((prev) =>
        prev.length === next.length && prev.every((command, i) => command === next[i])
          ? prev
          : next,
      );
    };

    const run = (tool: Tool) => {
      fieldRef.current?.focus();
      document.execCommand(tool.command, false);
      emit();
      readState();
    };

    const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
      // Before the line-break branch below, because the send shortcut is also an
      // Enter and this one has to win.
      if (onSubmit && SEND_MESSAGE.matches(event)) {
        event.preventDefault();
        onSubmit();
        return;
      }
      // "- " at the start of a line becomes a list, the way it does in every
      // other box people type lists into.
      if (event.key === ' ' && startList()) {
        event.preventDefault();
        emit();
        readState();
        return;
      }
      // Enter inside the editor is a new line, never a form submit.
      if (event.key === 'Enter') event.stopPropagation();
    };

    return (
      <div className={styles.editor}>
        <div className={styles.toolbar}>
          {TOOLS.map((tool) => (
            <button
              key={tool.command}
              type="button"
              className={active.includes(tool.command) ? styles.toolOn : styles.tool}
              aria-pressed={active.includes(tool.command)}
              // Keeps the selection alive: a button that takes focus first would
              // collapse it, and the mark would land on an empty caret.
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => run(tool)}
              disabled={disabled}
              title={tool.label}
              aria-label={tool.label}
            >
              <Icon name={tool.icon} size={15} filled={false} />
            </button>
          ))}
          {toolbarEnd && <div className={styles.toolbarEnd}>{toolbarEnd}</div>}
        </div>

        {staged}

        <div className={styles.field}>
          {empty && <span className={styles.placeholder}>{placeholder}</span>}
          <div
            ref={fieldRef}
            className={styles.input}
            contentEditable={!disabled}
            suppressContentEditableWarning
            role="textbox"
            aria-multiline="true"
            aria-label={placeholder}
            onInput={emit}
            onKeyUp={readState}
            onMouseUp={readState}
            onKeyDown={onKeyDown}
            onBlur={emit}
          />
        </div>

        {foot}
      </div>
    );
  },
);

/** The markers that start a list, and nothing else: see `richText.ts`. */
const LIST_MARKER = /^(?:[-*]|\d+[.)])$/;

/**
 * Turn a just-typed list marker into an actual list, and say whether it did.
 *
 * The editor is a WYSIWYG, so what a message will look like has to be what the
 * writer is looking at. Everywhere else in this product a line starting "- " is
 * a bullet — the plain composer sends the marker as markup and the thread draws
 * it as a list — and in here it sat as the two literal characters while the
 * toolbar's own list button produced something entirely different. The message
 * still went out as a list either way, which is what made it a trap rather than
 * a limitation: the editor was showing one thing and sending another.
 *
 * A numbered marker always starts the list at 1, as it does in every editor with
 * this rule: typing "7. " means "a numbered list", not "a list starting at 7".
 */
function startList(): boolean {
  const selection = window.getSelection();
  if (!selection?.isCollapsed || selection.rangeCount === 0) return false;

  const range = selection.getRangeAt(0);
  const node = range.startContainer;
  // Only ever a bare run of text, and only the marker at the head of its line:
  // `previousSibling` is what keeps "3 - 4" from turning into a bullet halfway
  // through a sentence.
  if (node.nodeType !== Node.TEXT_NODE || node.previousSibling !== null) return false;

  const typed = (node.textContent ?? '').slice(0, range.startOffset);
  if (!LIST_MARKER.test(typed)) return false;

  // Take the marker back out before the list goes in. It was shorthand for the
  // list, not part of the first item.
  const marker = document.createRange();
  marker.setStart(node, 0);
  marker.setEnd(node, range.startOffset);
  selection.removeAllRanges();
  selection.addRange(marker);
  document.execCommand('delete');

  document.execCommand(
    typed === '-' || typed === '*' ? 'insertUnorderedList' : 'insertOrderedList',
  );
  return true;
}

/**
 * Whether the placeholder should still be showing.
 *
 * Not simply "is there any text". Pressing Enter on an empty field leaves
 * `<div><br></div><div><br></div>` behind — two lines with no text in either —
 * and the placeholder used to sit there across a caret that had already moved to
 * the second one. Every browser also leaves a single `<div><br></div>` when a
 * draft is deleted, and that one really is empty, so the line count is what
 * separates the two cases: one blank line is nothing, two is a draft that starts
 * with a blank line.
 *
 * A list is never blank even with no words in it yet, because its marker is
 * already on screen and a placeholder would print across it.
 */
function isBlank(node: HTMLElement): boolean {
  if ((node.textContent ?? '').trim() !== '') return false;
  if (node.querySelector('li, img')) return false;
  return node.querySelectorAll('div, p').length <= 1;
}

/**
 * Focus with the caret after everything, which is where a handed-over draft
 * wants it: the writer was at the end of it a moment ago in the other field.
 */
function caretToEnd(node: HTMLElement): void {
  node.focus();
  const range = document.createRange();
  range.selectNodeContents(node);
  range.collapse(false);
  const selection = window.getSelection();
  selection?.removeAllRanges();
  selection?.addRange(range);
}
